import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useNavigate } from "react-router-dom";
import { Anchor, RefreshCw, ArrowUpRight, ChevronLeft } from "lucide-react";

const TAB_META = {
  boards:      { label: "Progress Board", color: "#5A90F5", path: "/boards" },
  capture:     { label: "Capture",        color: "#A855F7", path: "/capture" },
  commitments: { label: "Commitment",     color: "#FF8A00", path: "/commitments" },
  ab:          { label: "A/B Decision",   color: "#14B8A6", path: "/ab" },
};

function daysAgo(iso) {
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  return `${d} days ago`;
}

export default function BuoyBoard() {
  const { current } = useWorkspace();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const wsId = current?.workspace_id;

  const load = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/buoy`, { params: { days } });
      setItems(data.items || []);
    } catch {} finally { setLoading(false); }
  }, [wsId, days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A]">
      <div className="px-8 py-5 border-b border-[#1A1A1A] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-1.5 -ml-1.5 rounded-lg hover:bg-[#1a1a1a] text-[#71717A] hover:text-[#A1A1AA] transition-colors flex-shrink-0" title="Back to Overview">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="w-8 h-8 rounded-xl bg-[#FF8A00]/15 flex items-center justify-center">
            <Anchor className="w-4 h-4 text-[#FF8A00]"/>
          </div>
          <div>
            <h1 className="text-[18px] font-bold text-[#EDEDED]">Buoy Board</h1>
            <p className="text-[11px] text-[#3A3A3A]">{items.length} forgotten item{items.length!==1?"s":""} resurfaced</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#52525B]">Neglected over</span>
            <select value={days} onChange={e=>setDays(Number(e.target.value))}
              className="bg-[#111] border border-[#2A2A2A] rounded-lg px-2 py-1 text-[11px] text-[#EDEDED] outline-none">
              {[7,14,21,30,60].map(d=><option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
          <button onClick={load} className="p-2 rounded-xl hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA]">
            <RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`}/>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 text-[#3A3A3A] animate-spin"/></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#111] border border-[#2A2A2A] flex items-center justify-center">
              <Anchor className="w-7 h-7 text-[#3A3A3A]"/>
            </div>
            <div className="text-center">
              <p className="text-[14px] font-semibold text-[#52525B]">Nothing forgotten</p>
              <p className="text-[12px] text-[#3A3A3A] mt-1">Everything has been touched in the last {days} days</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2 max-w-2xl">
            <p className="text-[11px] text-[#3A3A3A] mb-4">These items haven't been touched in {days}+ days. Review, act on, or archive them.</p>
            {items.map(item => {
              const meta = TAB_META[item.tab] || TAB_META.capture;
              return (
                <div key={item.id} className="flex items-start gap-4 p-4 bg-[#111] border border-[#2A2A2A] rounded-xl hover:border-[#3A3A3A] transition-all group">
                  <div className="w-1.5 rounded-full self-stretch flex-shrink-0" style={{ background: meta.color }}/>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-[#EDEDED] truncate">{item.title}</p>
                    {item.subtitle && <p className="text-[11px] text-[#52525B] mt-0.5 truncate">{item.subtitle}</p>}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#1A1A1A] border border-[#2A2A2A]" style={{ color: meta.color }}>{meta.label}</span>
                      <span className="text-[10px] text-[#3A3A3A]">{daysAgo(item.created_at)}</span>
                    </div>
                  </div>
                  <button onClick={() => navigate(meta.path)}
                    className="p-2 rounded-xl hover:bg-[#1a1a1a] text-[#3A3A3A] hover:text-[#A1A1AA] opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                    title={`Go to ${meta.label}`}>
                    <ArrowUpRight className="w-3.5 h-3.5"/>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
